import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { getErrorMessage, createApiClient } from '../utils/api';
import './ResultsView.css';

interface Account {
  loan_number: string;
  class1_code: string;
  class2_code: string;
  current_principal_balance: number;
  current_payoff_balance: number;
  username: string;
}

interface UserResult {
  username: string;
  target_amount: number;
  actual_amount: number;
  accounts: Account[];
}

interface AllocationResults {
  [username: string]: UserResult;
}

interface UserWithManager {
  username: string;
  manager_username: string;
}

const ResultsView: React.FC = () => {
  const [results, setResults] = useState<AllocationResults | null>(null);
  const [usersWithManagers, setUsersWithManagers] = useState<UserWithManager[]>([]);
  const [expandedManagers, setExpandedManagers] = useState<Set<string>>(new Set());
  const [expandedUsers, setExpandedUsers] = useState<Set<string>>(new Set());

  // Expand all managers by default when results are loaded
  useEffect(() => {
    if (results && usersWithManagers.length > 0) {
      const usernameToManager = new Map<string, string>();
      usersWithManagers.forEach(u => {
        usernameToManager.set(u.username, u.manager_username || 'Unassigned');
      });
      
      const managers = new Set<string>();
      Object.keys(results).forEach(username => {
        const manager = usernameToManager.get(username) || 'Unassigned';
        managers.add(manager);
      });
      
      setExpandedManagers(managers);
    }
  }, [results, usersWithManagers]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();

  const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
  const downloadClient = createApiClient(60000); // 60 second timeout for Excel generation

  useEffect(() => {
    loadResults();
    loadUsersWithManagers();
  }, []);

  const loadResults = () => {
    const savedResults = sessionStorage.getItem('allocation-results');
    if (savedResults) {
      try {
        setResults(JSON.parse(savedResults));
      } catch (err) {
        setError('Failed to load results');
        console.error('Error loading results:', err);
      }
    } else {
      setError('No results found. Please run allocation first.');
    }
  };

  const loadUsersWithManagers = async () => {
    try {
      const apiClient = createApiClient(10000);
      const response = await apiClient.get('/users');
      
      if (response.data.users_with_managers) {
        setUsersWithManagers(response.data.users_with_managers);
      } else {
        // Fallback: create structure from simple users array
        setUsersWithManagers(
          response.data.users.map((username: string) => ({
            username,
            manager_username: 'Unassigned'
          }))
        );
      }
    } catch (err) {
      console.error('Error loading users with managers:', err);
      // Continue without manager info
    }
  };

  const toggleManager = (manager: string) => {
    const newExpanded = new Set(expandedManagers);
    if (newExpanded.has(manager)) {
      newExpanded.delete(manager);
    } else {
      newExpanded.add(manager);
    }
    setExpandedManagers(newExpanded);
  };

  const toggleUser = (username: string) => {
    const newExpanded = new Set(expandedUsers);
    if (newExpanded.has(username)) {
      newExpanded.delete(username);
    } else {
      newExpanded.add(username);
    }
    setExpandedUsers(newExpanded);
  };

  const handleDownloadExcel = async () => {
    if (!results) return;

    try {
      setLoading(true);
      
      // Get current configuration from cookies
      const savedConfig = localStorage.getItem('port-reallocation-config');
      if (!savedConfig) {
        alert('No configuration found. Please run allocation first.');
        return;
      }

      const config = JSON.parse(savedConfig);
      
      const response = await downloadClient.post('/download-excel', {
        users: config
      }, {
        responseType: 'blob'
      });

      // Create blob and download
      const blob = new Blob([response.data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      });
      
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = 'port_reallocation_results.xlsx';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
    } catch (err: any) {
      let errorMessage = getErrorMessage(err);
      if (err.code === 'ECONNABORTED' || err.message.includes('timeout')) {
        errorMessage = 'Request timed out. Excel generation is taking longer than expected.';
      }
      
      alert(errorMessage);
      console.error('Error downloading Excel:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleBackToConfig = () => {
    navigate('/port-reallocation/config');
  };

  if (error) {
    return (
      <div className="results-view">
        <div className="error">{error}</div>
        <button onClick={handleBackToConfig} className="back-button">
          Back to Configuration
        </button>
      </div>
    );
  }

  if (!results) {
    return (
      <div className="results-view">
        <div className="loading">Loading results...</div>
      </div>
    );
  }

  const allAccounts = Object.values(results).flatMap(userResult => userResult.accounts);
  const totalAccounts = allAccounts.length;
  const totalAmount = allAccounts.reduce((sum, account) => sum + account.current_principal_balance, 0);

  return (
    <div className="results-view">
      <div className="results-header">
        <h2>Port Reallocation Results</h2>
        <div className="results-summary">
          <div className="summary-item">
            <span className="summary-label">Total Accounts:</span>
            <span className="summary-value">{totalAccounts}</span>
          </div>
          <div className="summary-item">
            <span className="summary-label">Total Amount:</span>
            <span className="summary-value">${totalAmount.toLocaleString()}</span>
          </div>
        </div>
      </div>

      <div className="results-actions">
        <button onClick={handleBackToConfig} className="back-button">
          Back to Configuration
        </button>
        <button 
          onClick={handleDownloadExcel}
          disabled={loading}
          className="download-button"
        >
          {loading ? 'Generating...' : 'Download Excel File'}
        </button>
      </div>

      <div className="results-content">
        {(() => {
          if (!results || usersWithManagers.length === 0) {
            return Object.entries(results || {}).map(([username, userResult]) => (
              <div key={username} className="user-result">
                <div className="user-header">
                  <h3>{username}</h3>
                  <div className="user-summary">
                    <span>Target: ${userResult.target_amount.toLocaleString()}</span>
                    <span>Actual: ${userResult.actual_amount.toLocaleString()}</span>
                    <span>Accounts: {userResult.accounts.length}</span>
                  </div>
                </div>
                
                <div className="accounts-table-container">
                  <table className="accounts-table">
                    <thead>
                      <tr>
                        <th>Loan Number</th>
                        <th>Class 1</th>
                        <th>Class 2</th>
                        <th>Principal Balance</th>
                        <th>Payoff Balance</th>
                      </tr>
                    </thead>
                    <tbody>
                      {userResult.accounts.map((account, index) => (
                        <tr key={index}>
                          <td>{account.loan_number}</td>
                          <td>{account.class1_code}</td>
                          <td>{account.class2_code}</td>
                          <td>${account.current_principal_balance.toLocaleString()}</td>
                          <td>${account.current_payoff_balance.toLocaleString()}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            ));
          }

          // Create mapping of username to manager
          const usernameToManager = new Map<string, string>();
          usersWithManagers.forEach(u => {
            usernameToManager.set(u.username, u.manager_username || 'Unassigned');
          });

          // Group results by manager
          const groupedByManager = new Map<string, Array<[string, UserResult]>>();
          Object.entries(results).forEach(([username, userResult]) => {
            const manager = usernameToManager.get(username) || 'Unassigned';
            if (!groupedByManager.has(manager)) {
              groupedByManager.set(manager, []);
            }
            groupedByManager.get(manager)!.push([username, userResult]);
          });

          // Sort managers alphabetically
          const sortedManagers = Array.from(groupedByManager.keys()).sort();

          return sortedManagers.map(manager => {
            const userResults = groupedByManager.get(manager)!;
            const isManagerExpanded = expandedManagers.has(manager);
            const totalAccounts = userResults.reduce((sum, [, result]) => sum + result.accounts.length, 0);
            const totalAmount = userResults.reduce((sum, [, result]) => sum + result.actual_amount, 0);

            return (
              <div key={manager} className="manager-group">
                <div 
                  className="manager-header clickable"
                  onClick={() => toggleManager(manager)}
                >
                  <div>
                    <h3>{manager}</h3>
                    <span className="manager-count">
                      {userResults.length} account manager{userResults.length !== 1 ? 's' : ''} • {totalAccounts} accounts • ${totalAmount.toLocaleString()}
                    </span>
                  </div>
                  <span className="accordion-icon">
                    {isManagerExpanded ? '▼' : '▶'}
                  </span>
                </div>
                
                {isManagerExpanded && (
                  <div className="manager-users">
                    {userResults.map(([username, userResult]) => {
                      const isUserExpanded = expandedUsers.has(username);
                      return (
                        <div key={username} className="user-result">
                          <div 
                            className="user-header clickable"
                            onClick={() => toggleUser(username)}
                          >
                            <div>
                              <h4>{username}</h4>
                              <div className="user-summary">
                                <span>Target: ${userResult.target_amount.toLocaleString()}</span>
                                <span>Actual: ${userResult.actual_amount.toLocaleString()}</span>
                                <span>Accounts: {userResult.accounts.length}</span>
                              </div>
                            </div>
                            <span className="accordion-icon">
                              {isUserExpanded ? '▼' : '▶'}
                            </span>
                          </div>
                          
                          {isUserExpanded && (
                            <div className="accounts-table-container">
                              <table className="accounts-table">
                                <thead>
                                  <tr>
                                    <th>Loan Number</th>
                                    <th>Class 1</th>
                                    <th>Class 2</th>
                                    <th>Principal Balance</th>
                                    <th>Payoff Balance</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {userResult.accounts.map((account, index) => (
                                    <tr key={index}>
                                      <td>{account.loan_number}</td>
                                      <td>{account.class1_code}</td>
                                      <td>{account.class2_code}</td>
                                      <td>${account.current_principal_balance.toLocaleString()}</td>
                                      <td>${account.current_payoff_balance.toLocaleString()}</td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          });
        })()}
      </div>
    </div>
  );
};

export default ResultsView;
